import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import { message } from 'antd';
import { captureAnalytics } from '../actions/preload';
import useEmrPatientSearch from './useEmrPatientSearch';

const IDENTITY_DATE_FORMATS = [moment.ISO_8601, 'MM/DD/YYYY', 'YYYY-MM-DD'];

const normalizeIdentityDate = (value) => {
  if (!value) return null;

  const parsed = moment.isMoment(value)
    ? value
    : moment(value, IDENTITY_DATE_FORMATS, true);

  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const useEhrDrawerPatientSearch = ({
  credential,
  integrationType,
  config,
  location,
  allowResultSelection,
  requiresPatientResultSelection,
  selectedPatient,
  setSelectedPatient,
  patientIdentifierOverridesRef,
  form,
  applyFieldValues,
}) => {
  const [initialValues, setInitialValues] = useState({});
  const [initialValuesReady, setInitialValuesReady] = useState(false);
  // Schema-driven, mutually exclusive search methods (CareCloud: name+DOB vs
  // MRN). null means "the schema's default mode".
  const [searchModeKey, setSearchModeKey] = useState(null);
  const selectedPatientSourceValuesRef = useRef(null);
  const successfulSubmissionRef = useRef(false);
  const hadAcceptedSearchResultsRef = useRef(false);
  const abandonedSearchCapturedRef = useRef(false);
  const acceptedSearchRequestIdsRef = useRef(new Set());
  const ignoredSearchRequestIdsRef = useRef(new Set());
  const latestPatientSearchRequestIdRef = useRef(null);
  const loadedSchemaCredentialIdRef = useRef(null);
  const previousCredentialIdRef = useRef(null);
  const previousLocationRef = useRef(location);
  const selectedPatientSummaryRef = useRef(null);
  const identifierOverridesRef = patientIdentifierOverridesRef;

  const settings = useMemo(() => {
    if (!config) return null;

    return {
      minChars: config.min_chars ?? 2,
      debounceMs: config.debounce_ms,
      limit: config.result_limit ?? 10,
      fieldMappings: config.field_mappings || {},
      searchFields: config.search_fields || [],
      requiredGroups: config.required_groups || [],
      nameVariantSearchEnabled: config.name_variant_search_enabled === true,
      searchModes: config.search_modes || [],
      defaultSearchMode: config.default_search_mode,
      searchTrigger: config.search_trigger || 'automatic',
      autoSearchInitialValues: config.auto_search_initial_values === true,
    };
  }, [config]);

  const activeSearchMode = useMemo(() => {
    const modes = settings?.searchModes || [];
    if (!modes.length) return null;

    return modes.find((mode) => mode.key === searchModeKey)
      || modes.find((mode) => mode.key === settings.defaultSearchMode)
      || modes[0];
  }, [searchModeKey, settings]);

  // A mode narrows the schema's fields and required groups to its own keys;
  // schemas without modes search every configured field.
  const activeSearchFields = useMemo(() => {
    const fields = settings?.searchFields || [];
    const activeKeys = activeSearchMode?.field_keys;
    if (!activeKeys?.length) return fields;

    const keys = new Set(activeKeys);
    return fields.filter((field) => keys.has(field.key));
  }, [activeSearchMode, settings]);

  const activeRequiredGroups = useMemo(() => {
    const groups = settings?.requiredGroups || [];
    const activeKeys = activeSearchMode?.field_keys;
    if (!activeKeys?.length) return groups;

    const keys = new Set(activeKeys);
    return groups.filter((group) => group.every((key) => keys.has(key)));
  }, [activeSearchMode, settings]);

  const enabled = Boolean(settings) && Boolean(credential?.id);
  const isNextgenVariantSearch = Boolean(settings?.nameVariantSearchEnabled);
  const tracksSelectedPatientSource = isNextgenVariantSearch || requiresPatientResultSelection;
  const nextgenPracticeRequired = integrationType?.toLowerCase() === 'nextgen'
    && !String(location || '').trim();

  const search = useEmrPatientSearch({
    credentialId: credential?.id,
    enabled: enabled && !nextgenPracticeRequired,
    searchFields: activeSearchFields,
    minQueryLength: settings?.minChars,
    debounceMs: settings?.debounceMs,
    limit: settings?.limit,
    requiredGroups: activeRequiredGroups,
    location,
    initialValues,
    initialValuesReady,
    searchTrigger: settings?.searchTrigger,
    autoSearchInitialValues: settings?.autoSearchInitialValues,
  });

  const restoreSourceFields = useCallback((preservedValues = {}) => {
    const sourceValues = selectedPatientSourceValuesRef.current;
    selectedPatientSourceValuesRef.current = null;
    if (!sourceValues) return;

    applyFieldValues({ ...sourceValues, ...preservedValues });
  }, [applyFieldValues]);

  const clearSelectionState = useCallback(() => {
    setSelectedPatient(null);
    identifierOverridesRef.current = {};
    selectedPatientSourceValuesRef.current = null;
  }, [identifierOverridesRef, setSelectedPatient]);

  const prepareForSchemaLoad = useCallback(() => {
    setInitialValuesReady(false);
  }, []);

  const acceptSchemaPrefill = useCallback(({ schema, formValues, credentialId }) => {
    const searchFields = schema?.metadata?.patient_search?.search_fields || [];
    const structuredNameSearch = searchFields.some((field) => (
      ['first_name', 'last_name', 'date_of_birth'].includes(field.key)
    ));

    setInitialValues(structuredNameSearch ? {
      first_name: formValues.patient_first_name || '',
      last_name: formValues.patient_last_name || '',
      date_of_birth: normalizeIdentityDate(formValues.patient_dob) || '',
    } : {});
    loadedSchemaCredentialIdRef.current = credentialId;
    setInitialValuesReady(true);
  }, []);

  const resetLifecycle = useCallback(() => {
    setInitialValues({});
    setInitialValuesReady(false);
    setSearchModeKey(null);
    selectedPatientSourceValuesRef.current = null;
    successfulSubmissionRef.current = false;
    hadAcceptedSearchResultsRef.current = false;
    abandonedSearchCapturedRef.current = false;
    acceptedSearchRequestIdsRef.current.clear();
    ignoredSearchRequestIdsRef.current.clear();
    latestPatientSearchRequestIdRef.current = null;
    loadedSchemaCredentialIdRef.current = null;
    previousCredentialIdRef.current = null;
  }, []);

  useEffect(() => {
    const staleRequestId = latestPatientSearchRequestIdRef.current;
    if (previousCredentialIdRef.current && staleRequestId) {
      ignoredSearchRequestIdsRef.current.add(staleRequestId);
    }
    previousCredentialIdRef.current = credential?.id || null;

    restoreSourceFields();
    search.reset();
    setSelectedPatient(null);
    identifierOverridesRef.current = {};
    loadedSchemaCredentialIdRef.current = null;
  }, [credential?.id, identifierOverridesRef, restoreSourceFields, search.reset, setSelectedPatient]);

  useEffect(() => {
    latestPatientSearchRequestIdRef.current = search.requestId;
  }, [search.requestId]);

  // Patient search results are scoped to the practice used for their lookup.
  // Own this invalidation here so user-driven and programmatic location changes
  // both clear a selection before it can be submitted to another practice.
  useEffect(() => {
    const previousLocation = previousLocationRef.current;
    previousLocationRef.current = location;
    if (previousLocation === location) return;
    // Athena patients are practice-scoped; its location is the filing department.
    // The encounter picker owns department-dependent selection invalidation.
    if (integrationType?.toLowerCase() === 'athena') return;

    restoreSourceFields();
    clearSelectionState();
    search.reset();
  }, [clearSelectionState, integrationType, location, restoreSourceFields, search.reset]);

  useEffect(() => {
    if (
      !isNextgenVariantSearch
      || search.acceptedResponseSequence === 0
      || !search.requestId
      || String(loadedSchemaCredentialIdRef.current) !== String(credential?.id)
      || ignoredSearchRequestIdsRef.current.has(search.requestId)
      || acceptedSearchRequestIdsRef.current.has(search.requestId)
    ) return;

    acceptedSearchRequestIdsRef.current.add(search.requestId);
    if (search.results.length > 0) hadAcceptedSearchResultsRef.current = true;

    captureAnalytics('emr_patient_search_results_shown', {
      organization_id: credential?.organization_id,
      credential_id: credential?.id,
      integration_type: integrationType,
      match_kind: search.results[0]?.match_kind,
      warning_codes: search.searchWarnings,
      result_count: search.results.length,
      request_id: search.requestId,
    });
  }, [
    credential?.id,
    credential?.organization_id,
    integrationType,
    isNextgenVariantSearch,
    search.acceptedResponseSequence,
    search.requestId,
    search.results,
    search.searchWarnings,
  ]);

  useEffect(() => {
    if (enabled) return;

    restoreSourceFields();
    search.reset();
    setSelectedPatient(null);
    identifierOverridesRef.current = {};
  }, [enabled, identifierOverridesRef, restoreSourceFields, search.reset, setSelectedPatient]);

  useEffect(() => {
    if (allowResultSelection) return;

    restoreSourceFields();
    setSelectedPatient(null);
    identifierOverridesRef.current = {};
  }, [allowResultSelection, identifierOverridesRef, restoreSourceFields, setSelectedPatient]);

  const clearForMappedFormChange = useCallback((changedValues) => {
    if (!tracksSelectedPatientSource || !selectedPatient) return false;

    const mappedIdentityFields = new Set([
      ...Object.keys(config?.field_mappings || {}),
      'patient_first_name',
      'patient_last_name',
      'patient_dob',
      'patient_mrn',
      'mrn',
    ]);
    if (integrationType?.toLowerCase() === 'athena') mappedIdentityFields.delete('location');
    const identityChanged = Object.keys(changedValues).some((key) => mappedIdentityFields.has(key));
    if (!identityChanged) return false;

    captureAnalytics('emr_patient_search_selection_cleared', {
      credential_id: credential?.id,
      integration_type: integrationType,
      match_kind: selectedPatient.match_kind,
    });
    restoreSourceFields(changedValues);
    clearSelectionState();
    return true;
  }, [
    clearSelectionState,
    config?.field_mappings,
    credential?.id,
    integrationType,
    restoreSourceFields,
    selectedPatient,
    tracksSelectedPatientSource,
  ]);

  const changeField = useCallback((key, value, clearLegacySelection) => {
    if (allowResultSelection && (selectedPatient || Object.keys(identifierOverridesRef.current || {}).length > 0)) {
      if (tracksSelectedPatientSource) {
        restoreSourceFields();
      } else if (selectedPatient) {
        clearLegacySelection(selectedPatient);
      }
      clearSelectionState();
    }

    search.setFieldValue(key, value);
  }, [
    allowResultSelection,
    clearSelectionState,
    identifierOverridesRef,
    restoreSourceFields,
    search.setFieldValue,
    selectedPatient,
    tracksSelectedPatientSource,
  ]);

  const resetSearch = useCallback((clearLegacySelection) => {
    if (allowResultSelection && (selectedPatient || Object.keys(identifierOverridesRef.current || {}).length > 0)) {
      if (tracksSelectedPatientSource) {
        restoreSourceFields();
      } else if (selectedPatient) {
        clearLegacySelection(selectedPatient);
      }
      clearSelectionState();
    }

    search.reset();
  }, [
    allowResultSelection,
    clearSelectionState,
    identifierOverridesRef,
    restoreSourceFields,
    search.reset,
    selectedPatient,
    tracksSelectedPatientSource,
  ]);

  const changeSearchMode = useCallback((modeKey, clearLegacySelection) => {
    if (!modeKey || modeKey === activeSearchMode?.key) return false;
    if (!(settings?.searchModes || []).some((mode) => mode.key === modeKey)) return false;

    // Criteria and results belong to the previous method; start the new one clean.
    resetSearch(clearLegacySelection);
    setSearchModeKey(modeKey);
    return true;
  }, [activeSearchMode, resetSearch, settings]);

  const selectPatient = useCallback((patient, mapPatientToFields, onApplied) => {
    if (!allowResultSelection || !patient) return false;

    setSelectedPatient(patient);
    identifierOverridesRef.current = {};
    const updates = mapPatientToFields(patient);

    if (tracksSelectedPatientSource) {
      const sourceValues = selectedPatientSourceValuesRef.current || {};
      Object.keys(updates).forEach((fieldName) => {
        if (!Object.prototype.hasOwnProperty.call(sourceValues, fieldName)) {
          sourceValues[fieldName] = form.getFieldValue(fieldName);
        }
      });
      selectedPatientSourceValuesRef.current = sourceValues;
    }

    if (Object.keys(updates).length > 0) {
      applyFieldValues(updates);
      onApplied(Object.keys(updates));
      message.success('Patient details applied to the form.');
    }

    if (isNextgenVariantSearch) {
      captureAnalytics('emr_patient_search_candidate_selected', {
        credential_id: credential?.id,
        integration_type: integrationType,
        match_kind: patient.match_kind,
        warning_codes: search.searchWarnings,
        result_count: search.results.length,
        request_id: search.requestId,
      });
    }

    setTimeout(() => selectedPatientSummaryRef.current?.focus(), 0);
    return true;
  }, [
    allowResultSelection,
    applyFieldValues,
    credential?.id,
    form,
    integrationType,
    isNextgenVariantSearch,
    identifierOverridesRef,
    search.requestId,
    search.results.length,
    search.searchWarnings,
    setSelectedPatient,
    tracksSelectedPatientSource,
  ]);

  const clearPatient = useCallback((event, clearLegacySelection) => {
    event.stopPropagation();
    if (!allowResultSelection || !selectedPatient) return false;

    if (tracksSelectedPatientSource) {
      restoreSourceFields();
    } else {
      clearLegacySelection(selectedPatient);
    }
    clearSelectionState();

    if (isNextgenVariantSearch) {
      captureAnalytics('emr_patient_search_selection_cleared', {
        credential_id: credential?.id,
        integration_type: integrationType,
        match_kind: selectedPatient.match_kind,
      });
    }

    message.info('Patient selection cleared.');
    return true;
  }, [
    allowResultSelection,
    clearSelectionState,
    credential?.id,
    integrationType,
    isNextgenVariantSearch,
    restoreSourceFields,
    selectedPatient,
    tracksSelectedPatientSource,
  ]);

  const recordAbandonment = useCallback(() => {
    if (
      !isNextgenVariantSearch
      || !hadAcceptedSearchResultsRef.current
      || successfulSubmissionRef.current
      || abandonedSearchCapturedRef.current
    ) return;

    abandonedSearchCapturedRef.current = true;
    captureAnalytics('emr_patient_search_abandoned', {
      credential_id: credential?.id,
      integration_type: integrationType,
    });
  }, [credential?.id, integrationType, isNextgenVariantSearch]);

  const recordSubmission = useCallback(() => {
    successfulSubmissionRef.current = true;
    if (!isNextgenVariantSearch) return;

    captureAnalytics('emr_patient_search_upload_submitted', {
      credential_id: credential?.id,
      integration_type: integrationType,
      match_kind: selectedPatient?.match_kind,
    });
  }, [credential?.id, integrationType, isNextgenVariantSearch, selectedPatient?.match_kind]);

  return {
    fieldValues: search.fieldValues,
    results: search.results,
    searchWarnings: search.searchWarnings,
    hasMore: search.hasMore,
    acceptedResponseSequence: search.acceptedResponseSequence,
    requestId: search.requestId,
    isLoading: search.isLoading,
    error: search.error,
    canSearch: search.canSearch,
    search: search.search,
    settings,
    enabled,
    activeSearchFields,
    searchModes: settings?.searchModes || [],
    activeSearchMode: activeSearchMode?.key || null,
    changeSearchMode,
    isNextgenVariantSearch,
    nextgenPracticeRequired,
    selectedPatientSummaryRef,
    prepareForSchemaLoad,
    acceptSchemaPrefill,
    resetLifecycle,
    clearForMappedFormChange,
    changeField,
    resetSearch,
    selectPatient,
    clearPatient,
    recordAbandonment,
    recordSubmission,
  };
};

export default useEhrDrawerPatientSearch;
